import { useInfiniteQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { PostModel } from '@/common/models/post'
import { api, ApiError, APIPaginated } from '@/lib/axios'

type GetPinnedNewsProps = {
  userId: string
  page: number
}

async function getPinnedNews({ userId, page }: GetPinnedNewsProps) {
  const { data } = await api.backend.get<APIPaginated<PostModel>>(
    '/posts/paginate',
    {
      params: {
        limit: 15,
        page,
        relations: {
          where: {
            isActive: true,
            PostsPinned: {
              some: {
                pinnedById: userId,
              },
            },
          },
          orderBy: {
            createdAt: 'desc',
          },
          include: {
            PostsPinned: {
              where: {
                pinnedById: userId,
              },
            },
          },
        },
      },
    },
  )

  return data
}

export function usePinnedNews() {
  const { t } = useTranslation('panel')
  const { userInfo } = useGetUserInfo()

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<APIPaginated<PostModel>, AxiosError<ApiError>>({
    queryKey: ['/posts/paginate/pinned', userInfo?.id],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) =>
      getPinnedNews({
        userId: userInfo?.id || '',
        page: pageParam as number,
      }),
    getNextPageParam: (lastPage) => {
      const { currentPage, totalPages } = lastPage.meta
      return currentPage < totalPages ? currentPage + 1 : undefined
    },
  })

  if (error) {
    toast(Toast, {
      data: {
        type: 'danger',
        icon: OctagonAlert,
        title: t('news.use_pinned_posts.toast_error.title'),
        description:
          error?.response?.data?.friendlyMessage ||
          t('news.use_pinned_posts.toast_error.description'),
      },
    })
  }

  return {
    pinnedNews: data?.pages.flatMap((page) => page.items) || [],
    isLoadingPinnedNews: isLoading,
    totalPinnedNews: data?.pages[0]?.meta.totalItems || 0,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}
